import React from 'react';
import { Alert, Button, Empty } from 'antd';
import { BsPlusLg } from 'react-icons/bs';
import styles from '@pages/api-portal/styles.module.scss';
import { ApimPageShell } from '@pages/api-portal/apim-page-shell';
import { PageLoader } from '@pages/api-portal/page-loader';
import { useServicePrincipals } from './use-service-principals';
import { ServicePrincipalCard } from './service-principal-card';
import { CreateServicePrincipalDialog } from './create-dialog';
import { CreatedCredentialsDialog } from './created-credentials-dialog';
import { SERVICE_PRINCIPAL_DOMAIN } from '@pages/api-portal/constants';

/*
 * Admin screen for managing Try It Out service principals — create, list, and delete.
 * Creating one auto-provisions a DataStudio user with the ADMIN role.
 */
export function ServicePrincipalsScreen() {
    const {
        servicePrincipals,
        isLoading,
        isCreateDialogOpen,
        setIsCreateDialogOpen,
        createForm,
        setCreateForm,
        isCreating,
        createError,
        setCreateError,
        isCredentialsDialogOpen,
        createdPrincipal,
        handleOpenCreateDialog,
        handleCreate,
        handleCloseCredentialsDialog,
        handleDeleteRequest,
    } = useServicePrincipals();

    return (
        <ApimPageShell navSelectedId="service-principals" navType="admin">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                <div>
                    <header>
                        <h1>
                            <img src="/icons/key.svg" alt="" />
                            Service Principals
                        </h1>
                    </header>
                    <p className={styles.mutedText} style={{ margin: 0 }}>
                        {servicePrincipals.length} service principal{servicePrincipals.length !== 1 ? 's' : ''} configured
                    </p>
                </div>
                <Button type="primary" onClick={handleOpenCreateDialog}>
                    <BsPlusLg style={{ marginRight: 6 }} />
                    Create
                </Button>
            </div>

            <Alert
                style={{ marginBottom: 16 }}
                type="info"
                showIcon
                message={`Quick Start: Creating a service principal automatically generates a DataStudio user with username {name}@${SERVICE_PRINCIPAL_DOMAIN} and a secure auto-generated password. All service principals are assigned the ADMIN role.`}
            />

            {isLoading ? (
                <PageLoader />
            ) : servicePrincipals.length === 0 ? (
                <Empty description="No service principals yet">
                    <Button onClick={handleOpenCreateDialog}>
                        <BsPlusLg style={{ marginRight: 6 }} />
                        Create Your First Service Principal
                    </Button>
                </Empty>
            ) : (
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                        gap: 12,
                    }}
                >
                    {servicePrincipals.map((principal) => (
                        <ServicePrincipalCard key={principal.id} principal={principal} onRequestDelete={handleDeleteRequest} />
                    ))}
                </div>
            )}

            <CreateServicePrincipalDialog
                open={isCreateDialogOpen}
                onClose={() => setIsCreateDialogOpen(false)}
                form={createForm}
                onFormChange={(form) => {
                    setCreateForm(form);
                    setCreateError(null);
                }}
                isCreating={isCreating}
                error={createError}
                onSubmit={handleCreate}
            />
            <CreatedCredentialsDialog principal={isCredentialsDialogOpen ? createdPrincipal : null} onClose={handleCloseCredentialsDialog} />
        </ApimPageShell>
    );
}
